<template>
  <template v-if="!loading">
    <vl-grid v-vl-spacer:bottom.medium>
      <vl-column width="7" width-xs="12">
        <vl-title tag-name="h2"> {{ $t('deliveryspecification.detail.title') }} </vl-title>
      </vl-column>
      <vl-column v-if="editStore.canEdit" width="5" width-xs="12">
        <vl-action-group mod-align-right mod-space between>
          <vl-button
            v-if="items.length === 0"
            icon="plus"
            mod-icon-before
            :mod-disabled="editStore.isCreate(DeliveryProcessDefinitionEditType.DeliverySpecification)"
            @click="editStore.openCreate(DeliveryProcessDefinitionEditType.DeliverySpecification)"
          >
            {{ $t('general.add') }}
          </vl-button>
          <SpecificationActionButton
            v-if="items.length === 0"
            :detail="detail"
            :target="ImportSpecificationType.DeliverySpecification"
          />
        </vl-action-group>
      </vl-column>
    </vl-grid>
    <vl-grid
      v-if="items && items.length === 1"
      :class="{ edit: editStore.isEdit(DeliveryProcessDefinitionEditType.DeliverySpecification, items[0].id) }"
    >
      <vl-column width="8">
        <DeliverySpecificationEdit
          v-if="editStore.isEdit(DeliveryProcessDefinitionEditType.DeliverySpecification, items[0].id)"
          ref="editForm"
          :detail="items[0]"
        />
        <DeliverySpecificationRead v-else :detail="items[0]" :definition-id="definitionId" />
      </vl-column>
      <vl-column v-if="editStore.canEdit" width="4">
        <vl-action-group
          v-if="items[0] && !editStore.isEdit(DeliveryProcessDefinitionEditType.DeliverySpecification, items[0].id)"
          mod-align-right
          mod-space
          between
        >
          <vl-button
            icon="pencil"
            mod-icon-before
            mod-secondary
            :mod-disabled="editStore.editing"
            @click="editStore.openEdit(DeliveryProcessDefinitionEditType.DeliverySpecification, items[0].id)"
            >{{ $t('general.edit') }}</vl-button
          >
          <vl-button v-if="!hasChildren" icon="trash" mod-icon-before mod-error @click="showDeleteModal(items[0].id)">{{
            $t('general.delete')
          }}</vl-button>
        </vl-action-group>
        <vl-action-group v-else mod-align-right mod-space between>
          <vl-button icon="save" x mod-icon-before @click="save">{{ $t('general.save') }}</vl-button>
          <vl-button icon="cross" mod-icon-before mod-secondary mod-error @click="editStore.closeEdit">
            {{ $t('general.cancel') }}
          </vl-button>
        </vl-action-group>
      </vl-column>
    </vl-grid>
    <vl-accordion-list v-else-if="items && items.length">
      <vl-accordion-list-item v-for="(specification, i) in items" :id="i" :key="i">
        <vl-accordion :id="i.toString()" :title="getLabel(specification)">
          <vl-grid
            :class="{
              edit: editStore.isEdit(DeliveryProcessDefinitionEditType.DeliverySpecification, specification.id)
            }"
          >
            <vl-column width="8">
              <DeliverySpecificationEdit
                v-if="editStore.isEdit(DeliveryProcessDefinitionEditType.DeliverySpecification, specification.id)"
                ref="editForm"
                :detail="specification"
              />
              <DeliverySpecificationRead v-else :detail="specification" :definition-id="definitionId" />
            </vl-column>
            <vl-column v-if="editStore.canEdit" width="4">
              <vl-action-group
                v-if="
                  specification &&
                  !editStore.isEdit(DeliveryProcessDefinitionEditType.DeliverySpecification, specification.id)
                "
                mod-align-right
                mod-space
                between
              >
                <vl-button
                  icon="pencil"
                  mod-icon-before
                  mod-secondary
                  :mod-disabled="editStore.editing"
                  @click="editStore.openEdit(DeliveryProcessDefinitionEditType.DeliverySpecification, specification.id)"
                  >{{ $t('general.edit') }}</vl-button
                >
                <vl-button
                  v-if="!hasChildren"
                  icon="trash"
                  mod-icon-before
                  mod-error
                  @click="showDeleteModal(specification.id)"
                  >{{ $t('general.delete') }}</vl-button
                >
              </vl-action-group>
              <vl-action-group v-else mod-align-right mod-space between>
                <vl-button icon="save" x mod-icon-before :mod-loading="reloading" @click="save">{{
                  $t('general.save')
                }}</vl-button>
                <vl-button icon="cross" mod-icon-before mod-secondary mod-error @click="editStore.closeEdit">
                  {{ $t('general.cancel') }}
                </vl-button>
              </vl-action-group>
            </vl-column>
          </vl-grid>
        </vl-accordion>
      </vl-accordion-list-item>
    </vl-accordion-list>
    <vl-grid :class="{ edit: editStore.isCreate(DeliveryProcessDefinitionEditType.DeliverySpecification) }">
      <vl-column>
        <DeliverySpecificationCreate
          v-if="editStore.isCreate(DeliveryProcessDefinitionEditType.DeliverySpecification)"
        />
      </vl-column>
    </vl-grid>
    <template v-if="items && items.length">
      <vl-grid>
        <vl-column width="12">
          <DeliveryEntity :id="items[0].id" />
        </vl-column>
      </vl-grid>
      <vl-grid>
        <vl-column width="12">
          <DeliveryEntityLink :id="items[0].id" />
        </vl-column>
      </vl-grid>
    </template>
    <vl-grid v-else v-vl-spacer:bottom.medium>
      <vl-column>
        <vl-annotation>{{ $t('deliveryspecification.detail.empty') }}</vl-annotation>
      </vl-column>
    </vl-grid>
  </template>
</template>
<script lang="ts" setup>
import DeliveryEntity from '../delivery-entity/DeliveryEntity.vue';
import DeliveryEntityLink from '../delivery-entity-link/DeliveryEntityLink.vue';
import DeliverySpecificationCreate from './DeliverySpecificationCreate.vue';
import DeliverySpecificationRead from './DeliverySpecificationRead.vue';
import DeliverySpecificationEdit from './DeliverySpecificationEdit.vue';
import SpecificationActionButton from '../specification-action-button/SpecificationActionButton.vue';
import {
  DeliveryProcessDefinitionDetailDto,
  DeliverySpecificationDetailDto,
  ImportSpecificationType
} from '@/api/backoffice/clients';
import { useDeliverySpecificationStore } from '@/modules/configuration/store/delivery-specification/delivery-specification.store';
import { computed, ref, onMounted, Ref } from 'vue';
import { useRoute } from 'vue-router';
import { useDeliveryEntityStore } from '@/modules/configuration/store/delivery-entity/delivery-entity.store';
import { useDeliveryEntityLinkStore } from '@/modules/configuration/store/delivery-entity-link/delivery-entity-link.store';
import {
  DeliveryProcessDefinitionEditType,
  useDeliveryProcessDefinitionEditStore
} from '@/modules/configuration/store/delivery-process-definition/delivery-process-definition-edit.store';
import { useModalStore } from '@/modules/core/store/modal.store';
import { useI18n } from 'vue-i18n';

const store = useDeliverySpecificationStore();
const editStore = useDeliveryProcessDefinitionEditStore();
const deliveryEntityStore = useDeliveryEntityStore();
const deliveryEntitylinkStore = useDeliveryEntityLinkStore();
const modalStore = useModalStore();
const { t } = useI18n();
const route = useRoute();

const editForm: Ref<typeof DeliverySpecificationEdit | (typeof DeliverySpecificationEdit)[]> = ref(null);

defineProps<{
  detail: DeliveryProcessDefinitionDetailDto;
}>();

const loading = computed((): boolean => {
  return store.loading;
});

const reloading = computed((): boolean => {
  return store.reloading;
});

const items = computed((): DeliverySpecificationDetailDto[] => {
  return store.byDefinition(definitionId.value);
});

const definitionId = computed((): number => {
  return parseInt(route.params.id as string, 10);
});

const hasChildren = computed((): boolean => {
  const specificationId = items.value[0]?.id;
  const deliveryEntities = deliveryEntityStore.bySpecification(specificationId);
  const deliveryEntityLinks = deliveryEntitylinkStore.bySpecification(specificationId);
  return deliveryEntities.length > 0 || deliveryEntityLinks.length > 0;
});

onMounted(async () => {
  await store.getByDefinition(definitionId.value);
});

const save = () => {
  if (Array.isArray(editForm?.value)) {
    editForm.value[0].onSubmit();
  } else {
    editForm.value.onSubmit();
  }
};

const showDeleteModal = (specificationId: number) => {
  modalStore.showDeleteModal({
    title: t('deliveryspecification.detail.delete.title'),
    props: {
      confirm: () => store.remove(definitionId.value, specificationId)
    }
  });
};

const getLabel = (item: DeliverySpecificationDetailDto): string => {
  return `${item.label}`;
};
</script>
<style lang="scss" scoped>
:deep(.vl-accordion__panel) {
  padding-right: 0;
}
</style>
